import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Target, Pencil, Save, X, Plus, Trash2, Loader2 } from 'lucide-react';
import {
    useProductQuarterGoals,
    QUARTER_PROJECTION_MIN_DAYS,
    goalProgressPct,
    projectedQuarterPct,
    type ProductQuarterGoal,
    type ProductGoalKpiKey,
    type ProductQuarterGoalsConfig,
} from '@/hooks/useProductQuarterGoals';
import type { ProductPulseQuarter, ProductPulseQuarterToDate } from '@/hooks/useProductPulse';

const KPI_OPTIONS: { value: ProductGoalKpiKey; label: string }[] = [
    { value: 'registrations', label: 'Registros' },
    { value: 'captures', label: 'Capturas' },
    { value: 'active_users', label: 'Usuarios activos' },
    { value: 'onboarding_v2_completed', label: 'Onboarding OK' },
    { value: 'store_orders', label: 'Pedidos tienda' },
];

interface ProductQuarterGoalsPanelProps {
    quarter: ProductPulseQuarter | undefined;
    quarterToDate: ProductPulseQuarterToDate | undefined;
}

export function ProductQuarterGoalsPanel({ quarter, quarterToDate }: ProductQuarterGoalsPanelProps) {
    const fallbackLabel = quarter?.label ?? 'Trimestre actual';
    const { data: saved, saveGoals, isSaving } = useProductQuarterGoals(fallbackLabel);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<ProductQuarterGoalsConfig | null>(null);

    useEffect(() => {
        if (saved && !editing) {
            setDraft(saved);
        }
    }, [saved, editing]);

    if (!quarter || !quarterToDate || !draft) return null;

    const handleSave = async () => {
        if (!draft) return;
        await saveGoals({
            ...draft,
            quarter_label: quarter.label,
        });
        setEditing(false);
    };

    const updateGoal = (index: number, patch: Partial<ProductQuarterGoal>) => {
        setDraft(prev => {
            if (!prev) return prev;
            const goals = [...prev.goals];
            goals[index] = { ...goals[index], ...patch };
            return { ...prev, goals };
        });
    };

    const addGoal = () => {
        setDraft(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                goals: [
                    ...prev.goals,
                    {
                        id: `goal-${Date.now()}`,
                        label: 'Nuevo objetivo',
                        kpi_key: 'captures',
                        target: 100,
                    },
                ],
            };
        });
    };

    const removeGoal = (index: number) => {
        setDraft(prev => {
            if (!prev) return prev;
            return { ...prev, goals: prev.goals.filter((_, i) => i !== index) };
        });
    };

    return (
        <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Target size={18} className="text-primary" />
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">
                            Objetivos {quarter.label}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            Día {quarter.days_elapsed} de {quarter.days_total} del trimestre
                            {quarter.days_elapsed < QUARTER_PROJECTION_MIN_DAYS && (
                                <> · la proyección aparece a partir del día {QUARTER_PROJECTION_MIN_DAYS}</>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {editing ? (
                        <>
                            <button
                                type="button"
                                onClick={() => { setDraft(saved ?? draft); setEditing(false); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                            >
                                <X size={14} /> Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                            >
                                {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                Guardar
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                        >
                            <Pencil size={14} /> Editar objetivos
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-4">
                {draft.goals.map((goal, index) => {
                    const actual = quarterToDate[goal.kpi_key] ?? 0;
                    const progress = goalProgressPct(actual, goal.target);
                    const projected = projectedQuarterPct(
                        actual,
                        goal.target,
                        quarter.days_elapsed,
                        quarter.days_total
                    );

                    return (
                        <div key={goal.id} className="space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                {editing ? (
                                    <div className="flex flex-wrap items-center gap-2 flex-1">
                                        <input
                                            type="text"
                                            value={goal.label}
                                            onChange={e => updateGoal(index, { label: e.target.value })}
                                            className="flex-1 min-w-[120px] px-2 py-1 rounded-md bg-muted/50 border border-border text-sm"
                                        />
                                        <select
                                            value={goal.kpi_key}
                                            onChange={e => updateGoal(index, { kpi_key: e.target.value as ProductGoalKpiKey })}
                                            className="px-2 py-1 rounded-md bg-muted/50 border border-border text-xs"
                                        >
                                            {KPI_OPTIONS.map(o => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min={1}
                                            value={goal.target}
                                            onChange={e => updateGoal(index, { target: parseInt(e.target.value, 10) || 0 })}
                                            className="w-24 px-2 py-1 rounded-md bg-muted/50 border border-border text-sm font-mono"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeGoal(index)}
                                            className="p-1.5 text-muted-foreground hover:text-rose-400"
                                            aria-label="Eliminar objetivo"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="text-sm font-medium text-foreground">{goal.label}</span>
                                        <span className="text-xs text-muted-foreground font-mono">
                                            {actual.toLocaleString()} / {goal.target.toLocaleString()}
                                            {projected != null && (
                                                <span className={cn(
                                                    'ml-2 font-semibold',
                                                    projected >= 100 ? 'text-emerald-400' : projected >= 70 ? 'text-amber-400' : 'text-rose-400'
                                                )}>
                                                    proyección {projected}%
                                                </span>
                                            )}
                                        </span>
                                    </>
                                )}
                            </div>
                            {!editing && (
                                <div className="h-2 rounded-full bg-muted overflow-hidden">
                                    <div
                                        className={cn(
                                            'h-full rounded-full transition-all',
                                            progress >= 100 ? 'bg-emerald-500' : progress >= 60 ? 'bg-primary' : 'bg-amber-500'
                                        )}
                                        style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {editing && (
                <button
                    type="button"
                    onClick={addGoal}
                    className="mt-4 flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                    <Plus size={14} /> Añadir objetivo
                </button>
            )}
        </div>
    );
}
